import { CircularProgress, CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import SoftphoneWorkspace from "./SoftphoneWorkspace";
import OnboardingScreen from "./OnboardingScreen";
import BlockedStateScreen from "./BlockedStateScreen";
import PermissionsRenewalScreen from "./PermissionsRenewalScreen";
import TenantAdminScreen from "./TenantAdminScreen";
import { AppThemeName, useHostTheme } from "../hooks/useHostTheme";
import { useHostLocale } from "../hooks/useHostLocale";
import { useAppBootstrapFlow, UNAVAILABLE_SENTINEL } from "../hooks/useAppBootstrapFlow";
import { useTeamsAppSuspension } from "../hooks/useTeamsAppSuspension";
import { LocaleProvider, translate, renderMessage } from "../i18n/LocaleContext";
import AppFooter from "./AppFooter";
import SupportLink from "./SupportLink";

const themePalette: Record<AppThemeName, {
  accent: string;
  accentContrast: string;
  background: string;
  border: string;
  inputBackground: string;
  paper: string;
  textPrimary: string;
  textSecondary: string;
}> = {
  light: {
    accent: "#0f6cbd",
    accentContrast: "#ffffff",
    background: "#f6fbff",
    border: "rgba(17, 32, 44, 0.14)",
    inputBackground: "rgba(255, 255, 255, 0.92)",
    paper: "#ffffff",
    textPrimary: "#11202c",
    textSecondary: "#4f6778",
  },
  dark: {
    accent: "#79b8ff",
    accentContrast: "#081018",
    background: "#0d151d",
    border: "rgba(189, 208, 223, 0.22)",
    inputBackground: "rgba(19, 33, 45, 0.95)",
    paper: "#13212d",
    textPrimary: "#edf5fa",
    textSecondary: "#a8bfce",
  },
  contrast: {
    accent: "#ffff00",
    accentContrast: "#000000",
    background: "#000000",
    border: "#ffffff",
    inputBackground: "#000000",
    paper: "#000000",
    textPrimary: "#ffffff",
    textSecondary: "#ffff00",
  },
};

export default function App() {
  const { refreshHostTheme, registerThemeChangeHandler, themeName, themeStatus } = useHostTheme();
  const { locale, localeStatus } = useHostLocale();
  const t = useMemo(
    () => (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) => translate(locale, key, vars),
    [locale]
  );
  const [onboardingRequested, setOnboardingRequested] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    return ["admin_consent", "error", "tenant", "state", "scope"].some((key) => params.has(key));
  });
  const bootstrap = useAppBootstrapFlow({ enabled: !onboardingRequested, locale, localeStatus });
  // Tela de administração aberta a partir de um bloqueio (ramal ou licença ausentes). Aqui não há
  // softphone montado — a vista equivalente de dentro do workspace é a do SoftphoneWorkspace.
  const [tenantAdminFromBlocked, setTenantAdminFromBlocked] = useState(false);
  // Extraído do objeto porque o exhaustive-deps só verifica dependência em forma de membro
  // (bootstrap.sendAppVisibility) quando a raiz é uma prop; com uma variável local ele exigiria o
  // objeto inteiro, e aí o efeito re-rodaria a cada mudança de qualquer campo do bootstrap.
  const { sendAppVisibility, sendAppEventNotification } = bootstrap;

  // Eventos de ciclo de vida para o backend. Sempre com keepalive: os três são emitidos com o
  // documento saindo de cena, e sem ele o fetch morreria junto. Falha é engolida — no unload não
  // há mais ninguém para ver, e uma rejeição não tratada viraria linha de console capturada.
  const sendLifecycleEvent = useCallback(
    (type: "app.suspend" | "app.resume" | "app.unload", payload: Record<string, unknown> = {}) => {
      console.log(`[lifecycle] ${type} enviado`);
      return sendAppEventNotification({ type, payload }, undefined, { keepalive: true }).catch(() => {});
    },
    [sendAppEventNotification]
  );

  const theme = useMemo(() => {
    const palette = themePalette[themeName];
    const mode = themeName === "light" ? "light" : "dark";

    return createTheme({
      palette: {
        mode,
        primary: {
          main: palette.accent,
          contrastText: palette.accentContrast,
        },
        background: {
          default: palette.background,
          paper: palette.paper,
        },
        text: {
          primary: palette.textPrimary,
          secondary: palette.textSecondary,
        },
      },
      shape: {
        borderRadius: 14,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              textTransform: "none",
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: palette.inputBackground,
            },
            input: {
              color: palette.textPrimary,
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: palette.textSecondary,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: palette.border,
            },
            root: {
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.accent,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.accent,
              },
            },
          },
        },
      },
    });
  }, [themeName]);

  useEffect(() => {
    if (bootstrap.registrationStatus === "onboarding-required") {
      setOnboardingRequested(true);
    }
  }, [bootstrap.registrationStatus]);

  useEffect(() => {
    function handleVisibilityChange() {
      const visible = document.visibilityState === "visible";
      void sendAppVisibility(visible);

      // O Teams web não entrega themeChange (ver refreshHostTheme), então voltar a ficar visível é
      // um dos poucos gatilhos que temos para perceber que o tema do host mudou.
      if (visible) {
        void refreshHostTheme();
      }
    }

    // pagehide é o discriminador entre suspensão e descarga: na suspensão o host só esconde o
    // iframe e o documento continua vivo (nenhum pagehide); na descarga o iframe sai do DOM e
    // este é o último evento que o documento vê. Por isso o app.unload nasce aqui — é o que
    // permite ao backend liberar a presença "ocupado" de um softphone que acabou de morrer, em
    // vez de esperar os 30 min de expiração. O sinal de visibilidade continua sendo enviado por
    // compatibilidade, mas costuma cair no dedupe (o beforeSuspendOrTerminate ou o
    // visibilitychange já mandaram false); o backend deriva visible=false do próprio app.unload.
    function handlePageHide(event: PageTransitionEvent) {
      void sendAppVisibility(false);
      void sendLifecycleEvent("app.unload", {
        reason: "pagehide",
        persisted: event.persisted,
        visibility: document.visibilityState,
      });
    }

    // Com App Suspension, o mount deixa de ser garantia de "voltou a ficar visível" — a aplicação
    // pode ter sido apenas escondida e retornar sem recarregar. Quem cobre esse caso é o onResume
    // de useTeamsAppSuspension; esta chamada continua valendo para a abertura que de fato monta.
    handleVisibilityChange();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [refreshHostTheme, sendAppVisibility, sendLifecycleEvent]);

  // Opta a aplicação no App Suspension: ao trocar de app dentro do Teams, o host esconde o iframe
  // em vez de destruí-lo, preservando o realm do softphone e uma chamada em andamento. Nenhuma
  // destas callbacks pode escrever em estado que troque a branch renderizada abaixo ou a src do
  // iframe — ver a regra documentada em useTeamsAppSuspension.
  //
  // app.suspend / app.resume são informativos para o backend (só log): suspenso, o softphone
  // segue vivo e emitindo presence. A liberação de presença acontece no app.unload (pagehide).
  useTeamsAppSuspension({
    onBeforeSuspendOrTerminate: () =>
      Promise.all([sendAppVisibility(false), sendLifecycleEvent("app.suspend")]).then(() => undefined),
    onResume: () => {
      void sendAppVisibility(true);
      void sendLifecycleEvent("app.resume");
      // Relê o tema porque a suspensão pode ter durado uma mudança de tema inteira, e porque no
      // Teams web o evento themeChange não chega nem com o handler rearmado.
      void refreshHostTheme();
    },
    reregisterHostHandlers: registerThemeChangeHandler,
  });

  const shouldBlockForRegistration =
    !onboardingRequested &&
    bootstrap.softphoneConfig.status === "ready" &&
    (bootstrap.registrationStatus === "idle" || bootstrap.registrationStatus === "syncing");

  const shouldWaitForSoftphoneConfig =
    !onboardingRequested &&
    bootstrap.teamsSsoState.status === "ready" &&
    bootstrap.softphoneConfig.status === "loading";

  const loadingShell = (
    <div
      // O min-height mora no CSS (.app-loading-shell) porque precisa das duas
      // declarações vh/dvh como fallback, e style inline não aceita chave repetida.
      className="app-loading-shell"
      style={{
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: theme.palette.background.default,
        color: theme.palette.text.primary,
        textAlign: "center",
      }}
    >
      <CircularProgress size={32} />
    </div>
  );

  // Toda tela de erro precisa oferecer um caminho adiante, e a validação da loja exige
  // que o contato seja um hyperlink em vez de texto puro.
  const supportCta = (
    <p className="auth-gate-support">
      <SupportLink />
    </p>
  );

  // Administração de usuários aberta a partir de um bloqueio. Oferecida só a quem o check-in
  // sinalizou como administrador do tenant, e só nos bloqueios que ele resolve — os de origem
  // uTech (tenant pendente, bloqueado, permissões a renovar) não trazem o sinal, então a ação
  // nem aparece.
  const manageUsersAction = bootstrap.tenantAdminAccess
    ? () => setTenantAdminFromBlocked(true)
    : undefined;

  const tenantAdminScreen = tenantAdminFromBlocked ? (
    // Sem softphone montado: nenhuma faixa de chamada, e o voltar devolve à tela de bloqueio,
    // onde o "tentar novamente" refaz o check-in — o caminho natural depois de configurar o
    // próprio ramal.
    <div className="softphone-shell">
      <TenantAdminScreen
        authorizedFetch={bootstrap.authorizedFetch}
        currentUserId={bootstrap.teamsUser.user?.userId ?? ""}
        onBack={() => setTenantAdminFromBlocked(false)}
      />
      <AppFooter />
    </div>
  ) : null;

  const resolveDiagnosticValue = (value: string) => (value === UNAVAILABLE_SENTINEL ? t("common.unavailable") : value);

  const diagnostics = [
    ["teamsUser.status", bootstrap.teamsUser.status],
    ["teamsSsoState.status", bootstrap.teamsSsoState.status],
    ["hostName", resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.hostName)],
    ["clientType", resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.clientType)],
    ["frameContext", resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.frameContext)],
    ["pageUrl", resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.pageUrl)],
    [t("app.diagnostics.expectedManifestId"), bootstrap.teamsHostDiagnostics.expectedWebApplicationId],
    [t("app.diagnostics.expectedManifestResource"), bootstrap.teamsHostDiagnostics.expectedWebApplicationResource],
    [t("app.diagnostics.suggestedClientAppId"), resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.suggestedAuthorizedClientAppId)],
    [t("app.diagnostics.ssoErrorClientAppId"), resolveDiagnosticValue(bootstrap.teamsHostDiagnostics.ssoClientAppId)],
    [t("app.diagnostics.teamsContextError"), bootstrap.teamsHostDiagnostics.contextError || t("common.none")],
    [t("app.diagnostics.ssoAuthError"), bootstrap.teamsSsoState.errorDetail || t("common.none")],
  ] as const;

  return (
    <LocaleProvider locale={locale}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {themeStatus === "loading" ||
        (!onboardingRequested && bootstrap.teamsSsoState.status === "loading") ||
        shouldWaitForSoftphoneConfig ||
        shouldBlockForRegistration ? (
          loadingShell
        ) : onboardingRequested ? (
          <OnboardingScreen teamsUser={bootstrap.teamsUser.user} />
        ) : bootstrap.teamsSsoState.status !== "ready" ? (
          <div className="softphone-shell">
            <article className="status-card auth-gate-card">
              <div className="card-header">
                <h2>{t("app.accessUnavailable.title")}</h2>
              </div>
              <p className="auth-gate-copy">{renderMessage(t, bootstrap.teamsSsoState.detail)}</p>
              {supportCta}
              {/* Diagnóstico colapsado: continua disponível para o suporte, sem expor um
                  dump técnico como primeira coisa que o usuário final vê. */}
              <details className="auth-gate-details">
                <summary>{t("app.technicalDetails")}</summary>
                <dl className="auth-gate-diagnostics">
                  {diagnostics.map(([label, value]) => (
                    <div key={label} className="auth-gate-diagnostics-row">
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            </article>
            <AppFooter />
          </div>
        ) : tenantAdminScreen ? (
          tenantAdminScreen
        ) : bootstrap.registrationStatus === "user-not-configured" ? (
          <BlockedStateScreen
            titleKey="userNotConfigured.title"
            copyKey="userNotConfigured.copy"
            onRetry={bootstrap.refresh}
            onManageUsers={manageUsersAction}
          />
        ) : bootstrap.registrationStatus === "pending-approval" ? (
          <BlockedStateScreen
            titleKey="pendingApproval.title"
            copyKey="pendingApproval.copy"
            onRetry={bootstrap.refresh}
          />
        ) : bootstrap.registrationStatus === "access-blocked" ? (
          // Com retry: o bloqueio é administrado no msteams-ui e pode ser revertido; sem o botão, o
          // usuário só sairia desta tela recarregando o Teams.
          <BlockedStateScreen
            titleKey="accessBlocked.title"
            copyKey="accessBlocked.copy"
            onRetry={bootstrap.refresh}
          />
        ) : bootstrap.registrationStatus === "license-required" ? (
          <BlockedStateScreen
            titleKey="licenseRequired.title"
            copyKey="licenseRequired.copy"
            onRetry={bootstrap.refresh}
            onManageUsers={manageUsersAction}
          />
        ) : bootstrap.registrationStatus === "permissions-required" ? (
          <PermissionsRenewalScreen teamsUser={bootstrap.teamsUser.user} />
        ) : bootstrap.softphoneConfig.status === "error" ? (
          <div className="softphone-shell">
            <article className="status-card auth-gate-card">
              <div className="card-header">
                <h2>{t("app.sipConfigUnavailable.title")}</h2>
              </div>
              {/* Só o campo `message` do corpo de erro chega aqui — nunca o corpo cru, que já
                  expôs a URL do PABX com o token na tela do usuário. */}
              <p className="auth-gate-copy">
                {bootstrap.registrationBackendMessage || t("app.sipConfigUnavailable.fallbackDetail")}
              </p>
              {/* Referência para o suporte cruzar com o log do backend, sem dado sensível. */}
              <details className="auth-gate-details">
                <summary>{t("app.technicalDetails")}</summary>
                <dl className="auth-gate-diagnostics">
                  <div className="auth-gate-diagnostics-row">
                    <dt>{t("app.diagnostics.errorCode")}</dt>
                    <dd>{bootstrap.registrationErrorCode || t("common.none")}</dd>
                  </div>
                  <div className="auth-gate-diagnostics-row">
                    <dt>registrationStatus</dt>
                    <dd>{bootstrap.registrationStatus}</dd>
                  </div>
                  <div className="auth-gate-diagnostics-row">
                    <dt>{t("app.diagnostics.registrationDetail")}</dt>
                    <dd>{renderMessage(t, bootstrap.registrationDetail)}</dd>
                  </div>
                </dl>
              </details>
              {supportCta}
            </article>
            <AppFooter />
          </div>
        ) : (
          <SoftphoneWorkspace
            softphoneSipConfig={bootstrap.softphoneConfig.sip}
            pabxApiToken={bootstrap.softphoneConfig.pabxApiToken}
            permissions={bootstrap.softphoneConfig.permissions}
            sendAppEventNotification={bootstrap.sendAppEventNotification}
            sendSessionPoll={bootstrap.sendSessionPoll}
            pollIntervalSeconds={bootstrap.softphoneConfig.pollIntervalSeconds}
            tenantAdminAccess={bootstrap.tenantAdminAccess}
            currentUserId={bootstrap.teamsUser.user?.userId ?? ""}
            authorizedFetch={bootstrap.authorizedFetch}
          />
        )}
      </ThemeProvider>
    </LocaleProvider>
  );
}
